/**
 * The one place either rep surface talks to `/api/v1`.
 *
 * Shared by the widget and the standalone application because they differ in exactly two things - the
 * authorization scheme and where a replacement token comes from - and everything else is identical: problem
 * details, the correlation id, response validation, and the 401 retry. Two copies of that would drift, and
 * the drift would be in error handling, which is where nobody looks until it matters.
 *
 * Section 13 gives `/api/v1` both credentials: `Authorization: Widget <token>` for a brokered widget token
 * and `Authorization: Bearer <token>` for an identity provider access token. `scheme` is which one this
 * client presents.
 *
 * Three properties are worth knowing, and all three are consequences of these surfaces living on their own
 * origins:
 *
 *   - **The token is read at call time, never captured.** A refresh replaces it while requests are in flight,
 *     so a client that closed over the value would keep using the expired one.
 *   - **No cookies.** `credentials` is left alone deliberately: nothing here relies on one, which is what
 *     makes the widget survive storage partitioning (D-15). A cookie set by us inside a third-party frame may
 *     simply not be there.
 *   - **An absolute base.** The guest surface must never be framed and the widget must be, and one origin
 *     cannot carry both `frame-ancestors` policies - so requests are cross-origin and the API's CORS list has
 *     to name the caller.
 *
 * A 401 is special-cased: it means the token lapsed, which is not an error to put in front of a
 * representative. The caller is asked for a replacement and the request is retried once.
 */
import { documentContentUrlSchema, problemDetailsSchema } from '@iqz-ddds/contracts';
import type { ZodType } from 'zod';
import { uploadFile } from '../upload/tus';
import { xhrSend } from '../upload/xhr';
import type { UploadHandle, UploadProgress, UploadTransport } from '../upload/tus';

/** What a caller wants to be told while an upload is running. Both are optional. */
export interface UploadHooks {
  readonly onProgress?: (progress: UploadProgress) => void;
  /** The bytes are all sent and the scan has not finished. See DG.14. */
  readonly onChecking?: () => void;
}

const CORRELATION_HEADER = 'x-correlation-id';

/** JSON if the repository can delegate the read, the bytes if it cannot. See `ApiClient.download`. */
const DOWNLOAD_ACCEPT = 'application/json, */*';

/**
 * What a download turned out to be.
 *
 * A `url` is navigated to and a `blob` is saved, and the difference matters: only one of them can be
 * handed to `fetch` without the browser refusing it. See `ApiClient.download`.
 */
export type DownloadedContent =
  | { readonly kind: 'url'; readonly url: string }
  | { readonly kind: 'blob'; readonly blob: Blob };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly reference: string | undefined,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

/** Thrown when the network never reached us at all. Distinct, because the remedy is different. */
export class OfflineError extends Error {
  constructor() {
    super('The connection to the document service failed. Check your network and try again.');
    this.name = 'OfflineError';
  }
}

/**
 * Thrown when the token lapsed and a refresh did not produce a working one.
 *
 * The message is the caller's, because the remedy differs: an embedded panel needs the page containing it to
 * broker a new token, and the standalone application needs the representative to sign in again.
 */
export class SessionLapsedError extends Error {
  constructor(message = 'This session has ended. Sign in again.') {
    super(message);
    this.name = 'SessionLapsedError';
  }
}

function referenceOf(response: Response, body: unknown): string | undefined {
  const header = response.headers.get(CORRELATION_HEADER);
  if (header !== null && header !== '') return header;

  const parsed = problemDetailsSchema.safeParse(body);
  return parsed.success ? parsed.data.correlationId : undefined;
}

async function bodyOf(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export interface ClientOptions {
  readonly baseUrl: string;
  /**
   * Which of section 13's two credentials this client presents.
   *
   * Two named schemes rather than one, because the API validates them in completely different ways - a
   * bearer against the provider's JWKS, a widget token against a row - and a server that told them apart by
   * shape could be persuaded to guess wrong.
   */
  readonly scheme: 'Bearer' | 'Widget';
  /** Read per request, so a refresh mid-flight is picked up. */
  token(): string | undefined;
  /** Obtain a replacement token. Awaited once on a 401 before retrying. */
  refresh(): Promise<string | undefined>;
  /** What a lapsed session should say, since the remedy differs per surface. */
  readonly lapsedMessage?: string;
}

export class ApiClient {
  constructor(private readonly options: ClientOptions) {}

  async get<T>(path: string, schema: ZodType<T>): Promise<T> {
    return this.send(path, { method: 'GET' }, schema);
  }

  /**
   * `Idempotency-Key` is accepted rather than required by the interceptor, and sending one is worth the
   * trouble: without it a double-submitted form creates two contacts, or answers the second attempt with a
   * duplicate-address conflict that reads as the representative's mistake.
   *
   * The caller supplies the key because only the caller knows what makes two requests the same logical one.
   */
  async post<T>(
    path: string,
    body: unknown,
    schema: ZodType<T>,
    options?: { readonly idempotencyKey?: string },
  ): Promise<T> {
    return this.send(
      path,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(options?.idempotencyKey === undefined ? {} : { 'idempotency-key': options.idempotencyKey }),
        },
        body: JSON.stringify(body),
      },
      schema,
    );
  }

  async postVoid(path: string, body: unknown, options?: { readonly idempotencyKey?: string }): Promise<void> {
    await this.send(
      path,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(options?.idempotencyKey === undefined ? {} : { 'idempotency-key': options.idempotencyKey }),
        },
        body: JSON.stringify(body),
      },
      undefined,
    );
  }

  async delete(path: string, ifMatch?: string): Promise<void> {
    await this.send(
      path,
      { method: 'DELETE', ...(ifMatch === undefined ? {} : { headers: { 'if-match': ifMatch } }) },
      undefined,
    );
  }

  /**
   * A document's content: either somewhere to navigate to, or the bytes themselves.
   *
   * The `Accept` header names JSON first and then a wildcard - "a URL if you have one, the content
   * otherwise" - because both answers are legitimate and which one arrives depends on the repository
   * behind the API rather than on anything the caller knows. A repository that can mint a signed URL
   * hands one back; one that cannot streams, and there is nothing to navigate to.
   *
   * The caller must **navigate** to a returned URL and must never fetch it. This surface is cross-origin
   * to the API, so a scripted request that reaches the storage host - by following a redirect or by
   * fetching the URL directly - is CORS-checked against a bucket that cannot usefully be configured for
   * it: after a redirect the browser sends `Origin: null` rather than ours, which no allowlist can name
   * without naming every sandboxed frame on the web. A navigation is not checked at all.
   */
  async download(path: string): Promise<DownloadedContent> {
    const response = await this.raw(path, { method: 'GET', headers: { accept: DOWNLOAD_ACCEPT } });
    if (!response.ok) {
      const body = await bodyOf(response);
      throw new ApiError(response.status, detailOf(body, response.status), referenceOf(response, body));
    }

    if (!(response.headers.get('content-type') ?? '').startsWith('application/json')) {
      return { kind: 'blob', blob: await response.blob() };
    }

    // Validated, because this value becomes an `href`. A shape change reaching it unchecked is a
    // navigation to whatever the field happened to contain.
    const body = await bodyOf(response);
    const parsed = documentContentUrlSchema.safeParse(body);
    if (!parsed.success) {
      throw new ApiError(
        response.status,
        'The document service returned something unexpected.',
        referenceOf(response, body),
      );
    }
    return { kind: 'url', url: parsed.data.url };
  }

  /**
   * Endpoints and authorisation for a resumable upload into one exchange (DG.1).
   *
   * The protocol is `../upload/tus.ts`'s and is the same one the prospect surface speaks. What this
   * supplies is the three things that are ours: where the endpoints are, that the credential is a
   * header rather than a cookie, and what a failure says.
   *
   * The 401 retry is here rather than left to the tus loop, and it is not decoration. A representative's
   * access token lives five minutes and a 350MB transfer does not, so without this a large upload dies
   * part-way with a status the loop correctly treats as fatal, and the person is asked to send the whole
   * file again. Retrying is safe because a chunk body is a `Blob` slice rather than a stream, so it can
   * be read twice.
   */
  uploadTransport(exchangeId: string): UploadTransport {
    const authorised = async (url: string, init: RequestInit, token: string): Promise<Response> =>
      fetch(url, { ...init, headers: { ...init.headers, authorization: `${this.options.scheme} ${token}` } });

    /**
     * The 401-refresh-retry, over whichever issuer it is given.
     *
     * Parameterised because DG.21 added a second way to issue a request - XHR, for the chunk `PATCH`,
     * because `fetch` cannot report upload progress - and this retry is precisely the thing that must
     * not exist in two versions. A five-minute access token against a 350MB transfer is why it exists
     * at all, and the chunk `PATCH` is the request most likely to be the one that outlives the token.
     */
    const withRefresh = async (
      issue: (url: string, init: RequestInit, token: string) => Promise<Response>,
      url: string,
      init: RequestInit,
    ): Promise<Response> => {
      const token = this.options.token();
      if (token === undefined) throw this.lapsed();

      const first = await issue(url, init, token);
      if (first.status !== 401) return first;

      // One retry. More than one would loop against a provider that will not refresh.
      const refreshed = await this.options.refresh();
      if (refreshed === undefined) return first;
      return issue(url, init, refreshed);
    };

    return {
      createUrl: `${this.options.baseUrl}/api/v1/exchanges/${exchangeId}/uploads`,
      uploadsUrl: `${this.options.baseUrl}/api/v1/uploads`,

      send: (url, init) => withRefresh(authorised, url, init),

      // DG.21. Safe to retry for the same reason `send` is: a chunk body is a `Blob` slice rather than
      // a stream, so a second attempt reads the same bytes. The bar rewinds to the chunk's start on the
      // retry, which is honest - none of what it counted was accepted.
      sendWithProgress: (url, init, onSent) =>
        withRefresh(
          (u, i, token) => xhrSend(u, i, onSent, { headers: { authorization: `${this.options.scheme} ${token}` } }),
          url,
          init,
        ),

      failure: async (response, fallback) => {
        const body = await bodyOf(response);
        return new ApiError(response.status, detailOf(body, response.status) || fallback, referenceOf(response, body));
      },

      offline: () => new OfflineError(),
    };
  }

  /**
   * Send a document into an exchange, and wait for the pipeline's verdict.
   *
   * Composed here rather than in the view, because which endpoints exist and how they are authorised is
   * this class's business and nothing a component should assemble. The view supplies a file and gets
   * back what happened to it.
   */
  async upload(exchangeId: string, file: File, hooks?: UploadHooks): Promise<UploadHandle> {
    return uploadFile(this.uploadTransport(exchangeId), {
      file,
      ...(hooks?.onProgress === undefined ? {} : { onProgress: hooks.onProgress }),
      ...(hooks?.onChecking === undefined ? {} : { onChecking: hooks.onChecking }),
    });
  }

  private lapsed(): SessionLapsedError {
    return new SessionLapsedError(this.options.lapsedMessage);
  }

  private async raw(path: string, init: RequestInit): Promise<Response> {
    const token = this.options.token();
    if (token === undefined) throw this.lapsed();

    try {
      return await fetch(`${this.options.baseUrl}${path}`, {
        ...init,
        headers: { ...init.headers, authorization: `${this.options.scheme} ${token}` },
      });
    } catch {
      throw new OfflineError();
    }
  }

  private async send<T>(path: string, init: RequestInit, schema: ZodType<T> | undefined): Promise<T> {
    let response = await this.raw(path, init);

    if (response.status === 401) {
      /*
       * The token lapsed. Not an error to put in front of a representative.
       *
       * One retry. More than one would loop against a host that cannot broker or a provider that will not
       * refresh. For the widget, reaching here at all means `expiring` did not do its job, since that fires a
       * minute early; for the standalone application it is the ordinary case, because the realm gives an
       * access token five minutes.
       */
      const refreshed = await this.options.refresh();
      if (refreshed === undefined) throw this.lapsed();
      response = await this.raw(path, init);
      if (response.status === 401) throw this.lapsed();
    }

    const body = await bodyOf(response);

    if (!response.ok) {
      throw new ApiError(response.status, detailOf(body, response.status), referenceOf(response, body));
    }

    if (schema === undefined) return undefined as T;

    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      // A shape change surfaces as one error here rather than as `undefined` rendered three components
      // away. The reference is still attached, because this is the failure most worth reporting to us.
      throw new ApiError(
        response.status,
        'The document service returned something unexpected.',
        referenceOf(response, body),
      );
    }
    return parsed.data;
  }
}

/**
 * The server's sentence, never ours.
 *
 * `api-failures.ts` was careful about this copy - it never says which authorization rule refused, and never
 * distinguishes "no such record" from "not yours" - so replacing it with something of our own would throw
 * that away. The fallback is only for a response with no problem body at all.
 */
function detailOf(body: unknown, status: number): string {
  const parsed = problemDetailsSchema.safeParse(body);
  if (parsed.success && parsed.data.detail !== undefined && parsed.data.detail !== '') return parsed.data.detail;
  return status >= 500
    ? 'The document service had a problem. Try again shortly.'
    : 'That request could not be completed.';
}
